import { useSidebarContext } from '@/components/Sidebar/hooks/useSidebarContext'
import { useLiquidGlass } from './use-liquid-glass'
import { type SidebarLiquidPreset, sidebarLiquidPresets, type SidebarLiquidConfig } from '@/lib/liquid-glass-presets'

/**
 * Hook especializado para Liquid Glass en componentes del Sidebar
 *
 * @description
 * Wrapper sobre `useLiquidGlass` que:
 * - Usa presets optimizados para sidebar
 * - Accede al contexto del sidebar (navRef)
 * - Simplifica la API para componentes sidebar
 *
 * @example
 * ```tsx
 * function SidebarFluidIndicator() {
 *   const glass = useSidebarLiquidGlass('indicator')
 *
 *   return (
 *     <svg>
 *       <filter id="liquid-glass">
 *         <feImage href={glass.dataUri} />
 *         <feDisplacementMap scale={glass.filterAttributes.red.scale} />
 *       </filter>
 *     </svg>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Con overrides
 * const glass = useSidebarLiquidGlass('tooltip', {
 *   blur: 12,
 *   enableChromaticAberration: true
 * })
 * ```
 */
export function useSidebarLiquidGlass(
  preset: SidebarLiquidPreset,
  overrides?: Partial<SidebarLiquidConfig>
) {
  const { navRef } = useSidebarContext()

  // Merge preset con overrides
  const config = {
    ...sidebarLiquidPresets[preset],
    ...overrides
  }

  // Extraer enableChromaticAberration antes de separar geometry/visual
  const enableChromaticAberration = config.enableChromaticAberration ?? false

  // Generar liquid glass con preset
  const glass = useLiquidGlass(
    // GeometryConfig
    {
      width: config.width,
      height: config.height,
      radius: config.radius,
      border: config.border
    },
    // VisualConfig
    {
      frost: config.frost,
      blur: config.blur,
      blurBackground: config.blurBackground,
      displace: config.displace,
      scale: config.scale,
      alpha: config.alpha,
      lightness: config.lightness,
      x: config.x,
      y: config.y,
      blend: config.blend,
      r: config.r,
      g: config.g,
      b: config.b
    },
    enableChromaticAberration
  )

  // Retornar glass data + sidebar context
  return {
    ...glass,
    containerRef: navRef,
    preset,
    config
  }
}
